import React, { useState, useEffect, useCallback } from 'react';
import Topbar from '../components/Topbar';
import { getAirports, createAirport, deleteAirport, getSingleAirport, updateAirport, getConcourses, addConcourse, updateConcourse, deleteConcourse, getFloors, addFloor, updateFloor, deleteFloor, updateAirportStatus } from '../services/airportService';
import { getCountries, getStates, getCities } from '../services/commonService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';
const extractErrorMessage = (err, fallback = "An error occurred") => {
  if (!err) return fallback;
  const data = err.response?.data || err.data;
  if (data) {
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    if (typeof data.error === 'string' && data.error.trim()) return data.error;
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.map(e => typeof e === 'object' ? (e.msg || e.message || JSON.stringify(e)) : e).join(', ');
    }
    if (typeof data.errors === 'object' && data.errors !== null) {
      return Object.values(data.errors).flat().join(', ');
    }
  }
  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  return fallback;
};

const AirportManagement = () => {
  const { refreshKey } = useRefresh();
  const imgBaseUrl = process.env.REACT_APP_IMG_BASE_URL;
  const [airportsList, setAirportsList] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, currentPage: 1, limit: 10 });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    status: '',
  });

  // Location Data States
  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    rank: 1,
    name: '',
    country_id: '',
    city_id: '',
    timezone: 'America/New_York',
    lat: '',
    lng: '',
    center_lat: '',
    center_lng: '',
    terminal_count: 1,
    concourse_labels: [],
    floor_labels: [],
    boundary_points: [],
    max_airport_radius_meters: null,
    website_url: '',
    phone_number: '',
    address: '',
    internal_notes: '',
    status: 1,
    image: null
  });

  const handleAddBoundaryPointRow = () => {
    setFormData(prev => ({
      ...prev,
      boundary_points: [...(prev.boundary_points || []), { name: '', lat: '', lng: '' }]
    }));
  };

  const handleRemoveBoundaryPointRow = (index) => {
    setFormData(prev => ({
      ...prev,
      boundary_points: (prev.boundary_points || []).filter((_, i) => i !== index)
    }));
  };

  const handleBoundaryPointChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...(prev.boundary_points || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, boundary_points: updated };
    });
  };

  const [imagePreview, setImagePreview] = useState(null);
 
  const [concourseInput, setConcourseInput] = useState('');
  const [floorInput, setFloorInput] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'view', 'edit'
  const [viewData, setViewData] = useState(null);
  const [airportConcourses, setAirportConcourses] = useState([]);
  const [airportFloors, setAirportFloors] = useState([]);
  const [newConcourseName, setNewConcourseName] = useState('');
  const [newFloorName, setNewFloorName] = useState('');
  const [editingConcourse, setEditingConcourse] = useState(null); // { id, name }
  const [editingFloor, setEditingFloor] = useState(null); // { id, name }
  const [concourseDeleteModal, setConcourseDeleteModal] = useState({ show: false, id: null });
  const [floorDeleteModal, setFloorDeleteModal] = useState({ show: false, id: null });
  const [isConcourseSubmitting, setIsConcourseSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchAirportsData = useCallback(async (params = filters) => {
    setIsLoading(true);
    try {
      const response = await getAirports(params);
      if (response && response.result) {
        setAirportsList(response.data);
        setMeta(response.meta || {
          total: response.data?.length || 0,
          totalPages: 1,
          currentPage: params.page || 1,
          limit: params.limit || 10,
        });
      } else {
        toast.error(response?.message || "Failed to fetch airports");
      }
    } catch (error) {
      console.log('Error fetching airports:', error);
      // toast.error("An error occurred while fetching airports");
    } finally {
      setIsLoading(false);
    }
  }, []); // Remove filters dependency from useCallback to allow flexible calling

  const prevSearch = React.useRef(filters.search);

  // Unified Fetch Effect with Search Debounce
  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;
    
    // If search text changed, use 500ms debounce.
    // If only status or page changed, fetch immediately (0ms).
    const timer = setTimeout(() => {
      fetchAirportsData(filters);
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [filters, fetchAirportsData, refreshKey]);

  // Fetch Countries on Mount
  useEffect(() => {
    const loadCountries = async () => {
      const res = await getCountries();
      if (res && res.result) setCountries(res.data);
    };
    loadCountries();
  }, []);

  // Fetch States when Country changes
  useEffect(() => {
    if (formData.country_id) {
      const loadStates = async () => {
        const res = await getStates(formData.country_id);
        if (res && res.result) {
          setStatesList(res.data);
          // Only reset state if the current state selection is no longer valid or initial
          if (!res.data.find(s => s.id === parseInt(formData.state_id))) {
            setFormData(prev => ({ ...prev, state_id: '', city_id: '' }));
          }
        }
      };
      loadStates();
    } else {
      setStatesList([]);
    }
  }, [formData.country_id]);

  // Fetch Cities when State changes
  useEffect(() => {
    if (formData.state_id) {
      const loadCities = async () => {
        const res = await getCities(formData.state_id);
        if (res && res.result) {
          setCitiesList(res.data);
          if (!res.data.find(c => c.id === parseInt(formData.city_id))) {
            setFormData(prev => ({ ...prev, city_id: '' }));
          }
        }
      };
      loadCities();
    } else {
      setCitiesList([]);
    }
  }, [formData.state_id]);

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(airportsList.map(a => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedIds.length === 0) return;
    setIsLoading(true);
    try {
      const response = await updateAirportStatus({ ids: selectedIds, status });
      if (response && (response.status === 200 || response.status === 201 || response.data?.result)) {
        toast.success(`Status updated for ${selectedIds.length} airports`);
        setSelectedIds([]);
        fetchAirportsData();
      } else {
        toast.error(response?.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error(extractErrorMessage(error, "An error occurred during bulk status update"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCityChange = (e) => {
    const cityId = parseInt(e.target.value);
    const city = citiesList.find(c => c.id === cityId);
    setFormData(prev => ({
      ...prev,
      city_id: cityId || '',
      lat: city?.latitude || prev.lat,
      lng: city?.longitude || prev.lng
    }));
  };

  const dataURLtoBlob = (dataurl) => {
    if (!dataurl || !dataurl.includes(',')) return null;
    try {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } catch (e) {
      console.error("Error converting data URL to blob:", e);
      return null;
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'rank' || name === 'terminal_count' || name === 'status' || (name.includes('_id') && name !== 'city_id')) ? parseInt(value) || '' : value
    }));
  };

  const handleConcourseKeyDown = (e) => {
    if (e.key === 'Enter' && concourseInput.trim()) {
      e.preventDefault();
      if (!formData.concourse_labels.includes(concourseInput.trim())) {
        setFormData(prev => ({
          ...prev,
          concourse_labels: [...prev.concourse_labels, concourseInput.trim()]
        }));
      }
      setConcourseInput('');
    }
  };
 
  const handleFloorKeyDown = (e) => {
    if (e.key === 'Enter' && floorInput.trim()) {
      e.preventDefault();
      if (!formData.floor_labels.includes(floorInput.trim())) {
        setFormData(prev => ({
          ...prev,
          floor_labels: [...prev.floor_labels, floorInput.trim()]
        }));
      }
      setFloorInput('');
    }
  };
 
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        return toast.error("Image size should be less than 5MB");
      }
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeConcourseLabel = (label) => {
    setFormData(prev => ({
      ...prev,
      concourse_labels: prev.concourse_labels.filter(l => l !== label)
    }));
  };
 
  const removeFloorLabel = (label) => {
    setFormData(prev => ({
      ...prev,
      floor_labels: prev.floor_labels.filter(l => l !== label)
    }));
  };
 
  const handleSaveAirport = async (e) => {
    if (e) e.preventDefault();
    if (!formData.code || !formData.name || !formData.country_id || !formData.state_id || !formData.city_id) {
      return toast.error("Please fill in required fields (Code, Name, Country, State, City)");
    }

    if (formData.code.length > 10) {
      return toast.error("Airport code cannot exceed 10 characters");
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        lat: parseFloat(formData.lat) || 0,
        lng: parseFloat(formData.lng) || 0
      };

      const finalPayload = new FormData();
      Object.keys(payload).forEach(key => {
        // Exclude 'id' from payload in update mode as it's sent in the URL
        if (key === 'id' && viewMode === 'edit') return;

        if (payload[key] !== null && payload[key] !== undefined) {
          if (key === 'concourse_labels' && Array.isArray(payload[key])) {
            payload[key].forEach(label => finalPayload.append('concourse_labels[]', label));
          } else if (key === 'floor_labels' && Array.isArray(payload[key])) {
            payload[key].forEach(label => finalPayload.append('floor_labels[]', label));
          } else if (key === 'boundary_points' && Array.isArray(payload[key])) {
            finalPayload.append('boundary_points', JSON.stringify(payload[key]));
          } else if (key === 'image') {
            const imageValue = payload[key];
            if (imageValue instanceof File || imageValue instanceof Blob) {
              finalPayload.append('image', imageValue);
            } else if (typeof imageValue === 'string' && imageValue.startsWith('data:image')) {
              const blob = dataURLtoBlob(imageValue);
              if (blob) finalPayload.append('image', blob, 'airport_logo.png');
            }
          } else {
            finalPayload.append(key, payload[key]);
          }
        }
      });

      const response = viewMode === 'edit' ? await updateAirport(formData.id, finalPayload) : await createAirport(finalPayload);
      console.log('response : ', response);
      if (response && (response.data?.result || response.status === 200 || response.status === 201)) {
        toast.success(response?.data?.message || response?.message || `Airport ${viewMode === 'edit' ? 'updated' : 'created'} successfully`);
        setFormData({
          code: '', rank: 1, name: '', country_id: '', state_id: '', city_id: '',
          timezone: 'America/New_York', lat: '', lng: '', center_lat: '', center_lng: '', terminal_count: 1,
          concourse_labels: [], floor_labels: [], boundary_points: [], max_airport_radius_meters: null,
          website_url: '', phone_number: '', internal_notes: '', status: 1, image: null
        });
        setConcourseInput('');
        setFloorInput('');
        setNewConcourseName('');
        setNewFloorName('');
        setEditingConcourse(null);
        setEditingFloor(null);
        setAirportConcourses([]);
        setAirportFloors([]);
        setImagePreview(null);
        fetchAirportsData();
        setViewMode('list');
      } else {
        const errorMsg = extractErrorMessage(response, `Failed to ${viewMode === 'edit' ? 'update' : 'create'} airport`);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Failed to save airport:', error);
      const errorMsg = extractErrorMessage(error, `Failed to ${viewMode === 'edit' ? 'update' : 'create'} airport`);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDetails = async (id) => {
    setIsLoading(true);
    try {
      const res = await getSingleAirport(id);
      if (res && res.result) {
        setViewData(res.data);
        setViewMode('view');
      } else {
        toast.error(res?.message || "Failed to fetch airport details");
      }
    } catch (error) {
      console.log("An error occurred while fetching details : ", error);
      // toast.error("An error occurred while fetching details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = async (id) => {
    setIsLoading(true);
    try {
      const [res, concRes, floorRes] = await Promise.all([
        getSingleAirport(id),
        getConcourses(id),
        getFloors(id)
      ]);

      if (res && res.result) {
        const data = res.data;
        if (concRes && concRes.result) {
          setAirportConcourses(concRes.data);
        } else {
          setAirportConcourses([]);
        }
        if (floorRes && floorRes.result) {
          setAirportFloors(floorRes.data);
        } else {
          setAirportFloors([]);
        }
        setFormData({
          id: data.id,
          code: data.code,
          rank: data.rank,
          name: data.name,
          country_id: data.country_id,
          state_id: data.state_id,
          city_id: data.city_id,
          timezone: data.timezone,
          lat: data.lat,
          lng: data.lng,
          center_lat: data.center_lat || '',
          center_lng: data.center_lng || '',
          terminal_count: data.terminal_count,
          website_url: data.website_url || '',
          phone_number: data.phone_number || '',
          address: data.address || '',
          internal_notes: data.internal_notes || '',
          status: data.status,
          image: data.image || null,
          concourse_labels: [],
          floor_labels: data.floor_labels || [],
          boundary_points: (data.boundary_points || []).map(p => ({
            id: p.id,
            name: p.name || '',
            lat: p.lat || '',
            lng: p.lng || '',
            distance_from_center: p.distance_from_center || 0
          })),
          max_airport_radius_meters: data.max_airport_radius_meters || null
        });
        setImagePreview(data.image ? `${imgBaseUrl}${data.image}` : null);
        setViewMode('edit');
      } else {
        toast.error(res?.message || "Failed to fetch airport details for editing");
      }
    } catch (error) {
      console.log("An error occurred while fetching details : ", error);
      // toast.error("An error occurred while fetching details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteModal({ show: true, id });
  };

  const confirmDelete = async () => {
    if (!deleteModal.id) return;
    setIsDeleting(true);
    try {
      const res = await deleteAirport(deleteModal.id);
      if (res && res.result) {
        toast.success(res.message || "Airport deleted successfully");
        fetchAirportsData();
        setDeleteModal({ show: false, id: null });
      } else {
        toast.error(res?.message || "Failed to delete airport");
      }
    } catch (error) {
      console.log("An error occurred during deletion : ", error);
      toast.error(extractErrorMessage(error, "An error occurred during deletion"));
    } finally {
      setIsDeleting(false);
    }
  };

  const refreshConcourses = async () => {
    if (!formData.id) return;
    const res = await getConcourses(formData.id);
    if (res && res.result) setAirportConcourses(res.data);
  };

  const refreshFloors = async () => {
    if (!formData.id) return;
    const res = await getFloors(formData.id);
    if (res && res.result) setAirportFloors(res.data);
  };

  const handleAddConcourse = async () => {
    if (!newConcourseName.trim()) return;
    setIsConcourseSubmitting(true);
    try {
      const res = await addConcourse(formData.id, { name: newConcourseName });
      if (res && res.result) {
        toast.success("Concourse added successfully");
        setNewConcourseName('');
        refreshConcourses();
      } else {
        toast.error(res?.message || "Failed to add concourse");
      }
    } catch (error) {
      console.log("Error adding concourse : ", error);
      toast.error(extractErrorMessage(error, "Error adding concourse"));
    } finally {
      setIsConcourseSubmitting(false);
    }
  };

  const handleAddFloor = async () => {
    if (!newFloorName.trim()) return;
    setIsConcourseSubmitting(true);
    try {
      const res = await addFloor(formData.id, { name: newFloorName });
      if (res && res.result) {
        toast.success("Floor level added successfully");
        setNewFloorName('');
        refreshFloors();
      } else {
        toast.error(res?.message || "Failed to add floor level");
      }
    } catch (error) {
      console.log("Error adding floor level : ", error);
      toast.error(extractErrorMessage(error, "Error adding floor level"));
    } finally {
      setIsConcourseSubmitting(false);
    }
  };

  const handleUpdateConcourse = async () => {
    if (!editingConcourse || !editingConcourse.name.trim()) return;
    setIsConcourseSubmitting(true);
    try {
      const res = await updateConcourse({ id: editingConcourse.id, name: editingConcourse.name });
      if (res && res.result) {
        toast.success("Concourse updated successfully");
        setEditingConcourse(null);
        refreshConcourses();
      } else {
        toast.error(res?.message || "Failed to update concourse");
      }
    } catch (error) {
      console.log("Error updating concourse : ", error);
      toast.error(extractErrorMessage(error, "Error updating concourse"));
    } finally {
      setIsConcourseSubmitting(false);
    }
  };

  const handleUpdateFloor = async () => {
    if (!editingFloor || !editingFloor.name.trim()) return;
    setIsConcourseSubmitting(true);
    try {
      const res = await updateFloor({ id: editingFloor.id, name: editingFloor.name });
      if (res && res.result) {
        toast.success("Floor level updated successfully");
        setEditingFloor(null);
        refreshFloors();
      } else {
        toast.error(res?.message || "Failed to update floor level");
      }
    } catch (error) {
      console.log("Error updating floor level : ", error);
      toast.error(extractErrorMessage(error, "Error updating floor level"));
    } finally {
      setIsConcourseSubmitting(false);
    }
  };

  const handleDeleteConcourseConfirm = async () => {
    if (!concourseDeleteModal.id) return;
    setIsConcourseSubmitting(true);
    try {
      const res = await deleteConcourse(concourseDeleteModal.id);
      if (res && res.result) {
        toast.success("Concourse deleted successfully");
        setConcourseDeleteModal({ show: false, id: null });
        refreshConcourses();
      } else {
        toast.error(res?.message || "Failed to delete concourse");
      }
    } catch (error) {
      console.log("Error deleting concourse : ", error);
      toast.error(extractErrorMessage(error, "Error deleting concourse"));
    } finally {
      setIsConcourseSubmitting(false);
    }
  };

  const handleDeleteFloorConfirm = async () => {
    if (!floorDeleteModal.id) return;
    setIsConcourseSubmitting(true);
    try {
      const res = await deleteFloor(floorDeleteModal.id);
      if (res && res.result) {
        toast.success("Floor level deleted successfully");
        setFloorDeleteModal({ show: false, id: null });
        refreshFloors();
      } else {
        toast.error(res?.message || "Failed to delete floor level");
      }
    } catch (error) {
      console.log("Error deleting floor level : ", error);
      toast.error(extractErrorMessage(error, "Error deleting floor level"));
    } finally {
      setIsConcourseSubmitting(false);
    }
  };

  const renderPagination = () => {
    const { currentPage, totalPages } = meta;
    const pages = [];
    const range = 1; // Show current page and 1 page on either side

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || // First page
        i === totalPages || // Last page
        (i >= currentPage - range && i <= currentPage + range) // Range around current page
      ) {
        pages.push(i);
      } else if (
        i === currentPage - range - 1 ||
        i === currentPage + range + 1
      ) {
        pages.push('...');
      }
    }

    // Remove consecutive ellipses
    const uniquePages = pages.filter((item, index) => {
      return item !== '...' || pages[index - 1] !== '...';
    });

    return uniquePages.map((page, index) => (
      <div
        key={index}
        className={`page-btn ${currentPage === page ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
        onClick={() => page !== '...' && handlePageChange(page)}
      >
        {page}
      </div>
    ));
  };

  return (
    <>
      <Topbar title="Airport Management" subtitle={`Manage all ${meta.total} airports — status, details, and map configuration`} />
      <div className="content">
        <div className="screens-stack">

          {viewMode === 'list' && (
            <div>
              <div className="screen-label">3.1 — Airport List</div>

              <div className="card">
                <div className="toolbar">
                  <div className="search-wrap">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input
                      className="search-input"
                      placeholder="Search by airport name, code, or city..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                  </div>
                  <select
                    className="filter-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="1">Live</option>
                    <option value="2">Coming Soon</option>
                  </select>
                  <button className="btn btn-primary" onClick={() => setViewMode('add')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add Airport
                  </button>

                </div>

                {selectedIds.length > 0 && (
                  <div className="bulk-action-bar" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    background: '#EFF6FF', 
                    padding: '6px 24px', 
                    margin: '20px 0px 0px 0px',
                    animation: 'slideDown 0.3s ease-out',
                    borderBottom: '1px solid #D0E8FF',
                  }}>
                    <div style={{ color: '#1E40AF', fontWeight: 600, fontSize: '14px' }}>
                      {selectedIds.length} Airports selected
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button 
                        className="btn" 
                        style={{ background: '#DCFCE7', color: '#166534', border: 'none', padding: '6px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => handleBulkStatusUpdate(1)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        Activate All
                      </button>
                      <button 
                        className="btn" 
                        style={{ background: '#FEF3C7', color: '#92400E', border: 'none', padding: '6px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => handleBulkStatusUpdate(2)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        Deactivate All
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ overflowX: 'auto' }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>
                          <input 
                            type="checkbox" 
                            style={{ width: 14, height: 14, accentColor: 'var(--blue)', cursor: 'pointer' }} 
                            checked={airportsList.length > 0 && selectedIds.length === airportsList.length}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>Rank</th><th>Code</th><th>Airport Name</th><th>City, State</th><th>Address</th><th>Status</th><th>Date Added</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {airportsList.length > 0 ? airportsList.map((a, i) => (
                        <tr key={a.id || i}>
                          <td>
                            <input 
                              type="checkbox" 
                              style={{ width: 14, height: 14, accentColor: 'var(--blue)', cursor: 'pointer' }} 
                              checked={selectedIds.includes(a.id)}
                              onChange={() => handleSelectOne(a.id)}
                            />
                          </td>
                          <td><div className="rank-badge" style={{ background: a.rank === 1 ? 'var(--navy)' : 'var(--grey)' }}>{a.rank || '-'}</div></td>
                          <td><span className="code-badge">{a.code}</span></td>
                          <td><div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{a.name}</div></td>
                          <td style={{ fontSize: 13 }}>
                            {a.city_name || a.city || '-'}{ (a.state_name || a.state) ? `, ${a.state_name || a.state}` : (a.country_name ? `, ${a.country_name}` : '') }
                          </td>
                          <td style={{ fontSize: 13 }}>{a.address || '-'}</td>
                          <td><span className={`badge badge-${a.status === 1 ? 'live' : 'soon'}`}>{a.status_text || (a.status === 1 ? 'Live' : 'Coming Soon')}</span></td>
                          <td style={{ fontSize: 12, color: 'var(--grey)' }}>{a.date_added || (a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-')}</td>
                          <td>
                            <div className="action-btns">
                              <button className="action-btn" onClick={() => handleEditClick(a.id)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                              </button>

                              <button className="action-btn" onClick={() => handleViewDetails(a.id)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0z" /></svg>
                              </button>
                              <button className="action-btn danger" onClick={() => handleDeleteClick(a.id)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round"><path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>
                            {isLoading ? 'Fetching airports...' : 'No airports found.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="pagination">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div className="page-limit" style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--grey)', marginRight: 8 }}>Rows per page:</span>
                      <select
                        className="filter-select"
                        style={{ width: 'auto', height: 32, padding: '0 8px', borderRadius: '6px', fontSize: '12px' }}
                        value={filters.limit}
                        onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                      >
                        {[10, 50, 100, 500, 1000, ...(meta.total > 0 && ![10, 50, 100, 500, 1000].includes(meta.total) ? [meta.total] : [])].map(val => (
                          <option key={val} value={val}>{val}</option>
                        ))}
                      </select>
                    </div>
                    <div className="page-info">
                      Showing {(meta.currentPage - 1) * meta.limit + 1}–{Math.min(meta.currentPage * meta.limit, meta.total)} of {meta.total} airports
                    </div>
                  </div>
                  <div className="page-btns">
                    <button className="page-btn" disabled={meta.currentPage === 1} onClick={() => handlePageChange(meta.currentPage - 1)}>‹</button>
                    {renderPagination()}
                    <button className="page-btn" disabled={meta.currentPage === meta.totalPages} onClick={() => handlePageChange(meta.currentPage + 1)}>›</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(viewMode === 'add' || viewMode === 'edit') && (
            <div>
              <div className="screen-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{viewMode === 'add' ? '3.2 — Add New Airport' : '3.3 — Edit Airport'} &nbsp;·&nbsp; Airport Actions</span>
                <button className="btn btn-outline" onClick={() => setViewMode('list')} style={{ padding: '4px 12px', height: 32, fontSize: 12 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                  Back to List
                </button>
              </div>

              <div className="card">
                <form className="form-2col" onSubmit={handleSaveAirport}>
                  <div className="form-main">
                    <div className="fsec-title">Basic Information</div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Airport Code (IATA) <span className="req">*</span></label>
                        <input className="form-input" name="code" value={formData.code} onChange={handleFormChange} placeholder="e.g. CMH" required maxLength={10} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Rollout Rank <span className="req">*</span></label>
                        <input className="form-input" type="number" name="rank" value={formData.rank} onChange={handleFormChange} required />
                      </div>
                      <div className="form-group full">
                        <label className="form-label">Airport Name <span className="req">*</span></label>
                        <input className="form-input" name="name" value={formData.name} onChange={handleFormChange} placeholder="e.g. John Glenn Columbus Intl" required />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Country <span className="req">*</span></label>
                        <select className="form-select" name="country_id" value={formData.country_id} onChange={handleFormChange} required>
                          <option value="">Select Country</option>
                          {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">State <span className="req">*</span></label>
                        <select className="form-select" name="state_id" value={formData.state_id} onChange={handleFormChange} required disabled={!formData.country_id}>
                          <option value="">Select State</option>
                          {statesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">City <span className="req">*</span></label>
                        <select className="form-select" name="city_id" value={formData.city_id} onChange={handleCityChange} required disabled={!formData.state_id}>
                          <option value="">Select City</option>
                          {citiesList.map(city => <option key={city.id} value={city.id}>{city.name}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Timezone <span className="req">*</span></label>
                        <select className="form-select" name="timezone" value={formData.timezone} onChange={handleFormChange}>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Terminal Count <span className="req">*</span></label>
                        <input className="form-input" type="number" name="terminal_count" value={formData.terminal_count} onChange={handleFormChange} required />
                      </div>
                      {viewMode === 'add' && (
                        <>
                          <div className="form-group full">
                            <label className="form-label">Concourse Labels <span className="opt">Press Enter to add tag</span></label>
                            <div className="multi-select">
                              {formData.concourse_labels.map((tag, i) => (
                                <div key={i} className="ms-pill">
                                  {tag}
                                  <span onClick={() => removeConcourseLabel(tag)} style={{ cursor: 'pointer', marginLeft: 5 }}>✕</span>
                                </div>
                              ))}
                              <input
                                className="ms-input"
                                placeholder={formData.concourse_labels.length === 0 ? "e.g. Concourse A" : ""}
                                value={concourseInput}
                                onChange={(e) => setConcourseInput(e.target.value)}
                                onKeyDown={handleConcourseKeyDown}
                                style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, background: 'transparent' }}
                              />
                            </div>
                          </div>
                          <div className="form-group full">
                            <label className="form-label">Floors <span className="opt">Press Enter to add tag</span></label>
                            <div className="multi-select">
                              {formData.floor_labels.map((tag, i) => (
                                <div key={i} className="ms-pill">
                                  {tag}
                                  <span onClick={() => removeFloorLabel(tag)} style={{ cursor: 'pointer', marginLeft: 5 }}>✕</span>
                                </div>
                              ))}
                              <input
                                className="ms-input"
                                placeholder={formData.floor_labels.length === 0 ? "e.g. Level 1" : ""}
                                value={floorInput}
                                onChange={(e) => setFloorInput(e.target.value)}
                                onKeyDown={handleFloorKeyDown}
                                style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, background: 'transparent' }}
                              />
                            </div>
                          </div>
                        </>
                      )}

                    </div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>GPS Coordinates</div>
                    <div className="form-grid">
                      <div className="form-group"><label className="form-label">Latitude <span className="req">*</span></label><input className="form-input" name="lat" value={formData.lat} onChange={handleFormChange} placeholder="e.g. 39.99768" required /></div>
                      <div className="form-group"><label className="form-label">Longitude <span className="req">*</span></label><input className="form-input" name="lng" value={formData.lng} onChange={handleFormChange} placeholder="e.g. -82.88442" required /></div>
                    </div>
                    <div className="form-group full" style={{ marginTop: 16 }}><label className="form-label">Address <span className="opt">Optional</span></label><input className="form-input" name="address" value={formData.address} onChange={handleFormChange} /></div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>Contact & Web</div>
                    <div className="form-grid">
                      <div className="form-group"><label className="form-label">Website URL <span className="opt">Optional</span></label><input className="form-input" type="url" name="website_url" value={formData.website_url} onChange={handleFormChange} /></div>
                      <div className="form-group"><label className="form-label">Phone Number <span className="opt">Optional</span></label><input className="form-input" type="tel" name="phone_number" value={formData.phone_number} onChange={handleFormChange} /></div>
                      <div className="form-group full"><label className="form-label">Internal Notes <span className="opt">Optional</span></label><textarea className="form-textarea" name="internal_notes" value={formData.internal_notes} onChange={handleFormChange} /></div>
                    </div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>App Status</div>
                    {viewMode === 'edit' && (
                      <div style={{ marginBottom: 32 }}>
                        <div className="fsec-title" style={{ marginTop: 0, fontSize: 13, borderBottom: '1px solid #F1F5F9', paddingBottom: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            Linked Concourses
                            <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--grey)', marginTop: 2 }}>Manage concourses for this location</div>
                          </div>
                          <span className="badge badge-grey" style={{ fontSize: 10 }}>{airportConcourses.length} items</span>
                        </div>

                        <div className="concourse-manager">
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                            gap: 10,
                            marginBottom: 20
                          }}>
                            {airportConcourses.map(c => (
                              <div
                                key={c.id}
                                className="concourse-pill-v2"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  background: 'white',
                                  border: '1px solid #E2E8F0',
                                  padding: '6px 10px',
                                  borderRadius: 10,
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                  gridColumn: editingConcourse?.id === c.id ? 'span 2' : 'auto'
                                }}
                              >
                                {editingConcourse?.id === c.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                    <input
                                      value={editingConcourse.name}
                                      autoFocus
                                      onChange={(e) => setEditingConcourse({ ...editingConcourse, name: e.target.value })}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateConcourse();
                                        if (e.key === 'Escape') setEditingConcourse(null);
                                      }}
                                      style={{
                                        border: '1px solid var(--blue)',
                                        background: 'white',
                                        width: '100%',
                                        fontSize: 12,
                                        padding: '4px 8px',
                                        borderRadius: 6,
                                        outline: 'none',
                                        boxShadow: '0 0 0 2px rgba(30, 111, 191, 0.1)'
                                      }}
                                    />
                                    <div style={{ display: 'flex', gap: 2 }}>
                                      <button
                                        type="button"
                                        className="icon-action-btn success"
                                        onClick={handleUpdateConcourse}
                                        title="Save"
                                        style={{ color: 'var(--green)', background: '#ECFDF5' }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                      </button>
                                      <button
                                        type="button"
                                        className="icon-action-btn delete"
                                        onClick={() => setEditingConcourse(null)}
                                        title="Cancel"
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <span style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {c.name || c.label}
                                    </span>
                                    <div style={{ display: 'flex', gap: 2, marginLeft: 8 }}>
                                      <button
                                        type="button"
                                        className="icon-action-btn edit"
                                        onClick={() => setEditingConcourse({ id: c.id, name: c.name || c.label })}
                                        title="Edit Concourse"
                                      >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                      </button>
                                      <button
                                        type="button"
                                        className="icon-action-btn delete"
                                        onClick={() => setConcourseDeleteModal({ show: true, id: c.id })}
                                        title="Delete Concourse"
                                      >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>

                          {airportConcourses.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '15px', background: '#F8FAFC', borderRadius: 10, border: '1px dashed #E2E8F0', marginBottom: 20, fontSize: 12, color: 'var(--grey)' }}>
                              No concourses linked to this airport yet.
                            </div>
                          )}

                          <div className="add-concourse-ui" style={{
                            background: '#F8FAFC',
                            padding: '12px',
                            borderRadius: 12,
                            border: '1px solid #E2E8F0',
                            display: 'flex',
                            gap: 10
                          }}>
                            <div style={{ flex: 1, position: 'relative' }}>
                              <input
                                className="form-input-v2"
                                style={{
                                  width: '100%',
                                  height: 38,
                                  fontSize: 13,
                                  padding: '0 12px',
                                  borderRadius: 8,
                                  border: '1px solid #CBD5E1',
                                  background: 'white'
                                }}
                                placeholder="Add New Concourse (e.g. Concourse A)"
                                value={newConcourseName}
                                onChange={(e) => setNewConcourseName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddConcourse()}
                              />
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{ height: 38, padding: '0 15px', borderRadius: 8, fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                              onClick={handleAddConcourse}
                              disabled={isConcourseSubmitting}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                              Add
                            </button>
                          </div>
                        </div>

                        <div className="fsec-title" style={{ marginTop: 24, fontSize: 13, borderBottom: '1px solid #F1F5F9', paddingBottom: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          Linked Floor Levels
                          <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--grey)', marginTop: 2 }}>Manage floor levels for this location</div>
                        </div>
                        <span className="badge badge-grey" style={{ fontSize: 10 }}>{airportFloors.length} items</span>
                      </div>

                      <div className="concourse-manager">
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                          gap: 10,
                          marginBottom: 20
                        }}>
                          {airportFloors.map(f => (
                            <div
                              key={f.id}
                              className="concourse-pill-v2"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'white',
                                border: '1px solid #E2E8F0',
                                padding: '6px 10px',
                                borderRadius: 10,
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                gridColumn: editingFloor?.id === f.id ? 'span 2' : 'auto'
                              }}
                            >
                              {editingFloor?.id === f.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                  <input
                                    value={editingFloor.name}
                                    autoFocus
                                    onChange={(e) => setEditingFloor({ ...editingFloor, name: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdateFloor();
                                      if (e.key === 'Escape') setEditingFloor(null);
                                    }}
                                    style={{
                                      border: '1px solid var(--blue)',
                                      background: 'white',
                                      width: '100%',
                                      fontSize: 12,
                                      padding: '4px 8px',
                                      borderRadius: 6,
                                      outline: 'none',
                                      boxShadow: '0 0 0 2px rgba(30, 111, 191, 0.1)'
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: 2 }}>
                                    <button
                                      type="button"
                                      className="icon-action-btn success"
                                      onClick={handleUpdateFloor}
                                      title="Save"
                                      style={{ color: 'var(--green)', background: '#ECFDF5' }}
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                    </button>
                                    <button
                                      type="button"
                                      className="icon-action-btn delete"
                                      onClick={() => setEditingFloor(null)}
                                      title="Cancel"
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <span style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {f.name || f.label}
                                  </span>
                                  <div style={{ display: 'flex', gap: 2, marginLeft: 8 }}>
                                    <button
                                      type="button"
                                      className="icon-action-btn edit"
                                      onClick={() => setEditingFloor({ id: f.id, name: f.name || f.label })}
                                      title="Edit Floor Level"
                                    >
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                    </button>
                                    <button
                                      type="button"
                                      className="icon-action-btn delete"
                                      onClick={() => setFloorDeleteModal({ show: true, id: f.id })}
                                      title="Delete Floor Level"
                                    >
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>

                        {airportFloors.length === 0 && (
                          <div style={{ textAlign: 'center', padding: '15px', background: '#F8FAFC', borderRadius: 10, border: '1px dashed #E2E8F0', marginBottom: 20, fontSize: 12, color: 'var(--grey)' }}>
                            No floor levels linked to this airport yet.
                          </div>
                        )}

                        <div className="add-concourse-ui" style={{
                          background: '#F8FAFC',
                          padding: '12px',
                          borderRadius: 12,
                          border: '1px solid #E2E8F0',
                          display: 'flex',
                          gap: 10
                        }}>
                          <div style={{ flex: 1, position: 'relative' }}>
                            <input
                              className="form-input-v2"
                              style={{
                                width: '100%',
                                height: 38,
                                fontSize: 13,
                                padding: '0 12px',
                                borderRadius: 8,
                                border: '1px solid #CBD5E1',
                                background: 'white'
                              }}
                              placeholder="Add New Floor Level (e.g. Level 1)"
                              value={newFloorName}
                              onChange={(e) => setNewFloorName(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddFloor()}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ height: 38, padding: '0 15px', borderRadius: 8, fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                            onClick={handleAddFloor}
                            disabled={isConcourseSubmitting}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                    )}

                    <div className="toggle-row">
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>Airport Status</div>
                        <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>Live = fully mapped (1). Coming Soon = placeholder (2).</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--grey)' }}>Coming Soon</span>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status: prev.status === 1 ? 2 : 1 }))}
                          className={`toggle-switch ${formData.status === 1 ? 'on' : 'off'}`}
                        />
                        <span style={{ fontSize: 12, fontWeight: 700, color: formData.status === 1 ? 'var(--green)' : 'var(--grey)' }}>Live</span>
                      </div>
                    </div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>Geofence</div>
                    <div className="form-grid">
                      <div className="form-group"><label className="form-label">Center Latitude <span className="opt">Optional</span></label><input className="form-input" name="center_lat" value={formData.center_lat} onChange={handleFormChange} placeholder="e.g. 39.99768" /></div>
                      <div className="form-group"><label className="form-label">Center Longitude <span className="opt">Optional</span></label><input className="form-input" name="center_lng" value={formData.center_lng} onChange={handleFormChange} placeholder="e.g. -82.88442" /></div>
                    </div>

                    <div className="fsec-title" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        Airport Boundary Points (Geofence Perimeter)
                        <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--grey)', marginTop: 2 }}>
                          Add perimeter coordinates surrounding the airport center. Max radius is calculated automatically.
                          {formData.max_airport_radius_meters && (
                            <span className="badge badge-live" style={{ marginLeft: 8, fontSize: 10 }}>
                              Max Radius: {formData.max_airport_radius_meters} meters
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleAddBoundaryPointRow}
                        style={{ padding: '4px 12px', fontSize: 12, height: 30 }}
                      >
                        + Add Boundary Point
                      </button>
                    </div>

                    {(formData.boundary_points || []).length === 0 ? (
                      <div style={{ padding: '12px', background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 8, fontSize: 12, color: '#64748B', marginTop: 10, textAlign: 'center' }}>
                        No boundary points added yet. Click "+ Add Boundary Point" to add perimeter coordinates for geofence walk time.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                        {formData.boundary_points.map((pt, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F8FAFC', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', minWidth: 24 }}>#{idx + 1}</span>
                            <input
                              className="form-input"
                              placeholder="Label (e.g. North Gate)"
                              value={pt.name || ''}
                              onChange={(e) => handleBoundaryPointChange(idx, 'name', e.target.value)}
                              style={{ flex: 2, height: 34, fontSize: 12 }}
                            />
                            <input
                              className="form-input"
                              placeholder="Latitude (e.g. 40.0020)"
                              value={pt.lat || ''}
                              onChange={(e) => handleBoundaryPointChange(idx, 'lat', e.target.value)}
                              style={{ flex: 1.5, height: 34, fontSize: 12 }}
                            />
                            <input
                              className="form-input"
                              placeholder="Longitude (e.g. -82.8950)"
                              value={pt.lng || ''}
                              onChange={(e) => handleBoundaryPointChange(idx, 'lng', e.target.value)}
                              style={{ flex: 1.5, height: 34, fontSize: 12 }}
                            />
                            {pt.distance_from_center !== undefined && pt.distance_from_center !== null && (
                              <span style={{ fontSize: 11, color: '#059669', background: '#ECFDF5', padding: '4px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                                {pt.distance_from_center} m
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveBoundaryPointRow(idx)}
                              style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 4, fontWeight: 'bold' }}
                              title="Remove Point"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="form-group full" style={{ marginTop: 16 }}><label className="form-label">Address <span className="opt">Optional</span></label><input className="form-input" name="address" value={formData.address} onChange={handleFormChange} /></div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>Contact & Web</div>
                    <div className="form-grid">
                      <div className="form-group"><label className="form-label">Website URL <span className="opt">Optional</span></label><input className="form-input" type="url" name="website_url" value={formData.website_url} onChange={handleFormChange} /></div>
                      <div className="form-group"><label className="form-label">Phone Number <span className="opt">Optional</span></label><input className="form-input" type="tel" name="phone_number" value={formData.phone_number} onChange={handleFormChange} /></div>
                      <div className="form-group full"><label className="form-label">Internal Notes <span className="opt">Optional</span></label><textarea className="form-textarea" name="internal_notes" value={formData.internal_notes} onChange={handleFormChange} /></div>
                    </div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>App Status</div>
                    {viewMode === 'edit' && (
                      <div style={{ marginBottom: 32 }}>
                        <div className="fsec-title" style={{ marginTop: 0, fontSize: 13, borderBottom: '1px solid #F1F5F9', paddingBottom: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            Linked Concourses
                            <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--grey)', marginTop: 2 }}>Manage concourses for this location</div>
                          </div>
                          <span className="badge badge-grey" style={{ fontSize: 10 }}>{airportConcourses.length} items</span>
                        </div>

                        <div className="concourse-manager">
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                            gap: 10,
                            marginBottom: 20
                          }}>
                            {airportConcourses.map(c => (
                              <div
                                key={c.id}
                                className="concourse-pill-v2"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  background: 'white',
                                  border: '1px solid #E2E8F0',
                                  padding: '6px 10px',
                                  borderRadius: 10,
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                  gridColumn: editingConcourse?.id === c.id ? 'span 2' : 'auto'
                                }}
                              >
                                {editingConcourse?.id === c.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                    <input
                                      value={editingConcourse.name}
                                      autoFocus
                                      onChange={(e) => setEditingConcourse({ ...editingConcourse, name: e.target.value })}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleUpdateConcourse();
                                        if (e.key === 'Escape') setEditingConcourse(null);
                                      }}
                                      style={{
                                        border: '1px solid var(--blue)',
                                        background: 'white',
                                        width: '100%',
                                        fontSize: 12,
                                        padding: '4px 8px',
                                        borderRadius: 6,
                                        outline: 'none',
                                        boxShadow: '0 0 0 2px rgba(30, 111, 191, 0.1)'
                                      }}
                                    />
                                    <div style={{ display: 'flex', gap: 2 }}>
                                      <button
                                        type="button"
                                        className="icon-action-btn success"
                                        onClick={handleUpdateConcourse}
                                        title="Save"
                                        style={{ color: 'var(--green)', background: '#ECFDF5' }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                      </button>
                                      <button
                                        type="button"
                                        className="icon-action-btn delete"
                                        onClick={() => setEditingConcourse(null)}
                                        title="Cancel"
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <span style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {c.name || c.label}
                                    </span>
                                    <div style={{ display: 'flex', gap: 2, marginLeft: 8 }}>
                                      <button
                                        type="button"
                                        className="icon-action-btn edit"
                                        onClick={() => setEditingConcourse({ id: c.id, name: c.name || c.label })}
                                        title="Edit Concourse"
                                      >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                      </button>
                                      <button
                                        type="button"
                                        className="icon-action-btn delete"
                                        onClick={() => setConcourseDeleteModal({ show: true, id: c.id })}
                                        title="Delete Concourse"
                                      >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>

                          {airportConcourses.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '15px', background: '#F8FAFC', borderRadius: 10, border: '1px dashed #E2E8F0', marginBottom: 20, fontSize: 12, color: 'var(--grey)' }}>
                              No concourses linked to this airport yet.
                            </div>
                          )}

                          <div className="add-concourse-ui" style={{
                            background: '#F8FAFC',
                            padding: '12px',
                            borderRadius: 12,
                            border: '1px solid #E2E8F0',
                            display: 'flex',
                            gap: 10
                          }}>
                            <div style={{ flex: 1, position: 'relative' }}>
                              <input
                                className="form-input-v2"
                                style={{
                                  width: '100%',
                                  height: 38,
                                  fontSize: 13,
                                  padding: '0 12px',
                                  borderRadius: 8,
                                  border: '1px solid #CBD5E1',
                                  background: 'white'
                                }}
                                placeholder="Add New Concourse (e.g. Concourse A)"
                                value={newConcourseName}
                                onChange={(e) => setNewConcourseName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddConcourse()}
                              />
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary"
                              style={{ height: 38, padding: '0 15px', borderRadius: 8, fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                              onClick={handleAddConcourse}
                              disabled={isConcourseSubmitting}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                              Add
                            </button>
                          </div>
                        </div>

                        <div className="fsec-title" style={{ marginTop: 24, fontSize: 13, borderBottom: '1px solid #F1F5F9', paddingBottom: 10, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          Linked Floor Levels
                          <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--grey)', marginTop: 2 }}>Manage floor levels for this location</div>
                        </div>
                        <span className="badge badge-grey" style={{ fontSize: 10 }}>{airportFloors.length} items</span>
                      </div>

                      <div className="concourse-manager">
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                          gap: 10,
                          marginBottom: 20
                        }}>
                          {airportFloors.map(f => (
                            <div
                              key={f.id}
                              className="concourse-pill-v2"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'white',
                                border: '1px solid #E2E8F0',
                                padding: '6px 10px',
                                borderRadius: 10,
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                gridColumn: editingFloor?.id === f.id ? 'span 2' : 'auto'
                              }}
                            >
                              {editingFloor?.id === f.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                  <input
                                    value={editingFloor.name}
                                    autoFocus
                                    onChange={(e) => setEditingFloor({ ...editingFloor, name: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdateFloor();
                                      if (e.key === 'Escape') setEditingFloor(null);
                                    }}
                                    style={{
                                      border: '1px solid var(--blue)',
                                      background: 'white',
                                      width: '100%',
                                      fontSize: 12,
                                      padding: '4px 8px',
                                      borderRadius: 6,
                                      outline: 'none',
                                      boxShadow: '0 0 0 2px rgba(30, 111, 191, 0.1)'
                                    }}
                                  />
                                  <div style={{ display: 'flex', gap: 2 }}>
                                    <button
                                      type="button"
                                      className="icon-action-btn success"
                                      onClick={handleUpdateFloor}
                                      title="Save"
                                      style={{ color: 'var(--green)', background: '#ECFDF5' }}
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                                    </button>
                                    <button
                                      type="button"
                                      className="icon-action-btn delete"
                                      onClick={() => setEditingFloor(null)}
                                      title="Cancel"
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <span style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {f.name || f.label}
                                  </span>
                                  <div style={{ display: 'flex', gap: 2, marginLeft: 8 }}>
                                    <button
                                      type="button"
                                      className="icon-action-btn edit"
                                      onClick={() => setEditingFloor({ id: f.id, name: f.name || f.label })}
                                      title="Edit Floor Level"
                                    >
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                    </button>
                                    <button
                                      type="button"
                                      className="icon-action-btn delete"
                                      onClick={() => setFloorDeleteModal({ show: true, id: f.id })}
                                      title="Delete Floor Level"
                                    >
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>

                        {airportFloors.length === 0 && (
                          <div style={{ textAlign: 'center', padding: '15px', background: '#F8FAFC', borderRadius: 10, border: '1px dashed #E2E8F0', marginBottom: 20, fontSize: 12, color: 'var(--grey)' }}>
                            No floor levels linked to this airport yet.
                          </div>
                        )}

                        <div className="add-concourse-ui" style={{
                          background: '#F8FAFC',
                          padding: '12px',
                          borderRadius: 12,
                          border: '1px solid #E2E8F0',
                          display: 'flex',
                          gap: 10
                        }}>
                          <div style={{ flex: 1, position: 'relative' }}>
                            <input
                              className="form-input-v2"
                              style={{
                                width: '100%',
                                height: 38,
                                fontSize: 13,
                                padding: '0 12px',
                                borderRadius: 8,
                                border: '1px solid #CBD5E1',
                                background: 'white'
                              }}
                              placeholder="Add New Floor Level (e.g. Level 1)"
                              value={newFloorName}
                              onChange={(e) => setNewFloorName(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddFloor()}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ height: 38, padding: '0 15px', borderRadius: 8, fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                            onClick={handleAddFloor}
                            disabled={isConcourseSubmitting}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                    )}
                    <div className="toggle-row">
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>Airport Status</div>
                        <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>Live = fully mapped (1). Coming Soon = placeholder (2).</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--grey)' }}>Coming Soon</span>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status: prev.status === 1 ? 2 : 1 }))}
                          className={`toggle-switch ${formData.status === 1 ? 'on' : 'off'}`}
                        />
                        <span style={{ fontSize: 12, fontWeight: 700, color: formData.status === 1 ? 'var(--green)' : 'var(--grey)' }}>Live</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-aside">
                    <div className="fsec-title">Airport Logo / Photo</div>
                    <div
                      className="photo-upload"
                      style={{
                        marginBottom: 24,
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        border: imagePreview ? '1.5px solid var(--blue)' : '1.5px dashed #CBD5E1',
                        background: imagePreview ? '#F8FAFC' : 'var(--light)'
                      }}
                      onClick={() => document.getElementById('airport-image-upload').click()}
                    >
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                          <div className="photo-upload-label">Upload Airport Logo</div>
                          <div style={{ fontSize: 10, color: 'var(--grey)', marginTop: 4 }}>JPG, PNG • Max 5MB</div>
                        </>
                      )}
                      <input
                        type="file"
                        id="airport-image-upload"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </div>

                    {/* <div className="fsec-title">Airport Actions</div>
                  <div className="aside-actions">
                    {airportActions.map((a, i) => (
                      <div key={i} className={`aside-card ${a.cls}`}>
                        {a.label}
                        <div className="ac-sub">{a.sub}</div>
                      </div>
                    ))}
                  </div> */}
                  </div>
                </form>
                <div className="form-footer">
                  <button className="btn btn-outline" onClick={() => {
                    setFormData({ code: '', rank: 1, name: '', country_id: '', state_id: '', city_id: '', timezone: 'America/New_York', lat: '', lng: '', terminal_count: 1, concourse_labels: [], floor_labels: [], website_url: '', phone_number: '', address: '', internal_notes: '', status: 1, image: null });
                    setConcourseInput('');
                    setFloorInput('');
                    setNewConcourseName('');
                    setNewFloorName('');
                    setEditingConcourse(null);
                    setEditingFloor(null);
                    setAirportConcourses([]);
                    setAirportFloors([]);
                    setImagePreview(null);
                  }}>Reset</button>
                  <button className="btn btn-primary" onClick={handleSaveAirport} disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /></svg>
                        {viewMode === 'edit' ? 'Update Airport' : 'Save Airport'}
                      </>
                    )}
                  </button>

                </div>
              </div>
            </div>
          )}

          {viewMode === 'view' && viewData && (
            <div>
              <div className="screen-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>3.4 — Airport Details &nbsp;·&nbsp; {viewData.name}</span>
                <button className="btn btn-outline" onClick={() => setViewMode('list')} style={{ padding: '4px 12px', height: 32, fontSize: 12 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                  Back to List
                </button>
              </div>
              <div className="card">
                <div className="form-2col">
                  <div className="form-main">
                    <div className="fsec-title">Airport Information</div>
                    <div className="detail-grid">
                      <div className="detail-item"><label>Name</label><div>{viewData.name}</div></div>
                      <div className="detail-item"><label>Code</label><div>{viewData.code}</div></div>
                      <div className="detail-item"><label>Rollout Rank</label><div>#{viewData.rank || '-'}</div></div>
                      <div className="detail-item"><label>Status</label><div><span className={`badge badge-${viewData.status === 1 ? 'live' : 'soon'}`}>{viewData.status_text || (viewData.status === 1 ? 'Live' : 'Coming Soon')}</span></div></div>
                      <div className="detail-item"><label>Location</label><div>{viewData.city_details?.name || '-'}, {viewData.state_details?.name || '-'}, {viewData.country?.name || '-'}</div></div>
                      <div className="detail-item"><label>Timezone</label><div>{viewData.timezone || '-'}</div></div>
                      <div className="detail-item"><label>Terminals</label><div>{viewData.terminal_count || '-'}</div></div>
                      <div className="detail-item"><label>Concourses</label><div>{viewData.concourses?.length > 0 ? viewData.concourses.map(c => c.label || c.name || c).join(', ') : (viewData.concourse_labels?.join(', ') || '-')}</div></div>
                      <div className="detail-item"><label>Floors</label><div>{viewData.floor_labels?.join(', ') || '-'}</div></div>
                    </div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>GPS & Contact</div>
                    <div className="detail-grid">
                      <div className="detail-item"><label>Coordinates</label><div>{viewData.lat}, {viewData.lng}</div></div>
                      <div className="detail-item"><label>Website</label><div><a href={viewData.website_url} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>{viewData.website_url || '-'}</a></div></div>
                      <div className="detail-item"><label>Phone</label><div>{viewData.phone_number || '-'}</div></div>
                      <div className="detail-item"><label>Address</label><div>{viewData.address || '-'}</div></div>
                    </div>

                    <div className="fsec-title" style={{ marginTop: 24 }}>Geofence</div>
                    <div className="detail-grid">
                      <div className="detail-item"><label>Center Coordinates</label><div>{(viewData.center_lat && viewData.center_lng) ? `${viewData.center_lat}, ${viewData.center_lng}` : 'Not Set (Using Airport Coordinates)'}</div></div>
                      <div className="detail-item"><label>Max Airport Radius</label><div>{viewData.max_airport_radius_meters ? `${viewData.max_airport_radius_meters} meters` : '-'}</div></div>
                    </div>

                    <div className="fsec-title" style={{ marginTop: 16 }}>Airport Boundary Points (Geofence Perimeter)</div>
                    {(viewData.boundary_points || []).length === 0 ? (
                      <div style={{ fontSize: 13, background: '#f8fafc', padding: 12, borderRadius: 8, color: '#64748B' }}>
                        No perimeter boundary points configured for this airport.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginTop: 10 }}>
                        {viewData.boundary_points.map((pt, i) => (
                          <div key={i} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '10px 12px', borderRadius: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>{pt.name || `Point #${i + 1}`}</div>
                            <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>{pt.lat}, {pt.lng}</div>
                            {pt.distance_from_center !== undefined && (
                              <div style={{ fontSize: 11, color: '#059669', fontWeight: 600, marginTop: 4 }}>
                                Distance from center: {pt.distance_from_center} m
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {viewData.image && (
                      <div style={{ marginTop: 24 }}>
                        <div className="fsec-title">Airport Logo</div>
                        <div style={{ width: 120, height: 120, borderRadius: 12, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
                          <img src={`${imgBaseUrl}${viewData.image}`} alt="Airport Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      </div>
                    )}

                    <div className="fsec-title" style={{ marginTop: 24 }}>Internal Notes</div>
                    <div className="detail-content" style={{ fontSize: 13, background: '#f8fafc', padding: 15, borderRadius: 8, color: '#475569' }}>
                      {viewData.internal_notes || 'No internal notes provided.'}
                    </div>
                  </div>

                  <div className="form-aside">
                    <div className="fsec-title">Quick Stats</div>
                    <div className="aside-card ac-blue">Created By: {viewData.creator?.full_name || `User #${viewData.created_by}`}</div>
                    <div className="aside-card ac-grey">Date Added: {viewData.date_added || (viewData.createdAt ? new Date(viewData.createdAt).toLocaleDateString() : '-')}</div>
                    <div className="aside-card ac-grey">Last Updated: {viewData.updatedAt ? new Date(viewData.updatedAt).toLocaleString() : '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteModal.show && (
            <div className="modal-overlay">
              <div className="modal-card">
                <div className="modal-icon danger">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></svg>
                </div>
                <h3>Delete Airport?</h3>
                <p>Are you sure you want to remove this airport? This action cannot be undone and will affect all linked POIs.</p>
                <div className="modal-actions">
                  <button className="btn btn-outline" onClick={() => setDeleteModal({ show: false, id: null })} disabled={isDeleting}>Cancel</button>
                  <button className="btn btn-danger" onClick={confirmDelete} disabled={isDeleting}>
                    {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Concourse Delete Confirmation Modal */}
          {concourseDeleteModal.show && (
            <div className="modal-overlay">
              <div className="modal-card" style={{ width: 350 }}>
                <div className="modal-icon danger">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </div>
                <h3>Remove Concourse?</h3>
                <p>This will permanently decouple this concourse from this airport.</p>
                <div className="modal-actions">
                  <button className="btn btn-outline" onClick={() => setConcourseDeleteModal({ show: false, id: null })} disabled={isConcourseSubmitting}>Cancel</button>
                  <button className="btn btn-danger" onClick={handleDeleteConcourseConfirm} disabled={isConcourseSubmitting}>
                    {isConcourseSubmitting ? 'Deleting...' : 'Yes, Remove'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Floor Delete Confirmation Modal */}
          {floorDeleteModal.show && (
            <div className="modal-overlay">
              <div className="modal-card" style={{ width: 350 }}>
                <div className="modal-icon danger">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </div>
                <h3>Remove Floor Level?</h3>
                <p>This will permanently remove this floor level from this airport.</p>
                <div className="modal-actions">
                  <button className="btn btn-outline" onClick={() => setFloorDeleteModal({ show: false, id: null })} disabled={isConcourseSubmitting}>Cancel</button>
                  <button className="btn btn-danger" onClick={handleDeleteFloorConfirm} disabled={isConcourseSubmitting}>
                    {isConcourseSubmitting ? 'Deleting...' : 'Yes, Remove'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
      <style>{`
        .page-btn { cursor: pointer; }
        .page-btn.dots { cursor: default; border: none; background: transparent; }
        .page-btn.dots:hover { background: transparent; color: var(--grey); }
        .page-btn[disabled] { cursor: not-allowed; opacity: 0.5; }
        .ms-input::placeholder { color: #8A93A2; }

        /* Custom Styles for new features */
        .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .detail-item label { display: block; font-size: 11px; font-weight: 700; color: var(--grey); text-transform: uppercase; margin-bottom: 4px; }
        .detail-item div { font-size: 14px; color: var(--navy); font-weight: 500; }
        
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-card { background: white; width: 400px; padding: 30px; border-radius: 12px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
        .modal-icon { width: 50px; height: 50px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 15px; }
        .modal-icon.danger { background: #fee2e2; color: #dc2626; }
        .modal-card h3 { font-size: 18px; color: var(--navy); margin-bottom: 10px; }
        .modal-card p { font-size: 14px; color: var(--grey); line-height: 1.5; margin-bottom: 25px; }
        .modal-actions { display: flex; gap: 12px; justify-content: center; }
        .btn-danger { background: #dc2626; color: white; border: none; padding: 8px 20px; border-radius: 6px; font-weight: 600; cursor: pointer; }
        .btn-danger:hover { background: #b91c1c; }
        .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Concourse Management enhancements */
        .concourse-pill-v2:hover {
          border-color: var(--blue) !important;
          background: #F0F9FF !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05) !important;
        }

        .icon-action-btn {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          color: var(--grey);
        }

        .icon-action-btn.edit:hover {
          background: #EEF6FF;
          color: var(--blue);
        }

        .icon-action-btn.delete:hover {
          background: #FFF1F2;
          color: #EF4444;
        }

        .form-input-v2:focus {
          border-color: var(--blue) !important;
          box-shadow: 0 0 0 3px rgba(30, 111, 191, 0.1);
          outline: none;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {(isLoading || isSubmitting || isDeleting || isConcourseSubmitting) && <Spinner />}
    </>
  );
};

export default AirportManagement;